// Does a team match ESPN the way the Cloud Functions will look it up?
//
// Every ESPN lookup in functions/ (scores, record fill, kickoff checks,
// rankings) is EXACT, and tries two things (functions/lib/scoreboard.js
// sideMatches, lib/records.js, lib/rankings.js):
//   1. API Team Name  === ESPN's full name   ("Utah Utes")
//   2. Short Name     === ESPN's school name ("Utah", ESPN "location")
// A team that matches neither is silently skipped — Hawaii (ESPN "Hawai'i")
// and Appalachian State (ESPN "App State") were. findEspnMatch mirrors those
// rules exactly, so what Team Management shows is what the server will do.
// suggestEspnTeams is only for helping the admin pick a fix; it is looser on
// purpose and never used for real matching.

/**
 * @typedef {{ id: string, displayName: string, location: string }} EspnTeam
 */

/**
 * The ESPN team this team will match, and which field matched — or null.
 * @param {{ apiTeamName?: string, shortName?: string }} team
 * @param {EspnTeam[]} espnTeams
 * @returns {{ espnTeam: EspnTeam, via: 'apiTeamName' | 'shortName' } | null}
 */
export const findEspnMatch = (team, espnTeams) => {
  if (!team || !Array.isArray(espnTeams)) return null;
  const { apiTeamName, shortName } = team;
  if (apiTeamName) {
    const byName = espnTeams.find((t) => t.displayName === apiTeamName);
    if (byName) return { espnTeam: byName, via: 'apiTeamName' };
  }
  if (shortName) {
    const bySchool = espnTeams.find((t) => t.location === shortName);
    if (bySchool) return { espnTeam: bySchool, via: 'shortName' };
  }
  return null;
};

// Lowercase, strip accents and punctuation ("Hawai'i" -> "hawaii").
export const normalizeTeamName = (value) => (
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // accents (after NFD)
    .replace(/['\u2018\u2019\u02bb`]/g, '') // apostrophes and the okina join the word
    .replace(/[^a-z0-9 ]/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
);

const tokens = (value) => new Set(normalizeTeamName(value).split(' ').filter(Boolean));

/**
 * Closest ESPN teams for a team with no exact match, best first.
 * A spelling-only difference ("Hawaii" vs "Hawai'i") ranks highest; after
 * that, shared words in the full name (mascots discriminate well:
 * "Appalachian State Mountaineers" vs "App State Mountaineers").
 * @param {{ name?: string, apiTeamName?: string, shortName?: string }} team
 * @param {EspnTeam[]} espnTeams
 * @param {number} [limit]
 * @returns {EspnTeam[]}
 */
export const suggestEspnTeams = (team, espnTeams, limit = 3) => {
  if (!team || !Array.isArray(espnTeams)) return [];
  const fullName = team.apiTeamName || team.name || '';
  const wanted = tokens(fullName);
  const normFull = normalizeTeamName(fullName);
  const normShort = normalizeTeamName(team.shortName);
  if (wanted.size === 0 && !normShort) return [];

  const scoreOf = (espnTeam) => {
    if (normFull && normalizeTeamName(espnTeam.displayName) === normFull) return 100;
    if (normShort && normalizeTeamName(espnTeam.location) === normShort) return 90;
    const theirs = tokens(espnTeam.displayName);
    let shared = 0;
    for (const word of wanted) if (theirs.has(word)) shared += 1;
    const union = new Set([...wanted, ...theirs]).size;
    return union > 0 ? (shared / union) * 50 : 0;
  };
  const scored = espnTeams.map((espnTeam) => ({ espnTeam, score: scoreOf(espnTeam) }));

  return scored
    .filter((s) => s.score >= 20)
    .sort((a, b) => b.score - a.score || a.espnTeam.displayName.localeCompare(b.espnTeam.displayName))
    .slice(0, limit)
    .map((s) => s.espnTeam);
};

/**
 * Every team that won't match ESPN, with suggestions, sorted by name.
 * @param {Array<{ id: string, name?: string, apiTeamName?: string, shortName?: string }>} teams
 * @param {EspnTeam[]} espnTeams
 */
export const findUnmatchedTeams = (teams, espnTeams) => (
  (Array.isArray(teams) ? teams : [])
    .filter((team) => !findEspnMatch(team, espnTeams))
    .map((team) => ({ team, suggestions: suggestEspnTeams(team, espnTeams) }))
    .sort((a, b) => String(a.team.name || '').localeCompare(String(b.team.name || '')))
);
